import { useContext, Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Container } from 'reactstrap';
import { Button } from 'reactstrap';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { TenantContext } from '../../../context/TenantContext';
import { ContactEntries } from './ContactEntries';
import { ModalContactAddOrUpdate } from './ModalContactAddOrUpdate';
import * as Utils from '../../../utils/helper';
import * as Misc from '../../../utils/misc';
import * as apiCalls from '../../../logic/apiCalls';
import { ButtonWithDisabledTooltip } from '../../../components/ButtonWithDisabledTooltip';
import { Alert } from 'react-bootstrap';

// The maximum allowed number of contacts per tenant.
const maximumNumberOfContacts = 100;

class ContactSettingsInternal extends Component {
    state = {
        isLoading: true,
        contacts: [],
        contactToDelete: {},
        contactToUpdate: {},
        isConfirmDeleteModalOpen: false,
        isContactModalOpen: false,
        isContactModalUpdate: false,
    }

    async componentDidMount() {
        this.getContacts();
    }

    async componentWillUnmount() {
    }

    showConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: true,
        });
    };

    hideConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: false,
        });
    };

    showAddContactModal = () => {
        this.setState({
            isContactModalUpdate: false,
            isContactModalOpen: true,
        });
    };

    showUpdateContactModal = () => {
        this.setState({
            isContactModalUpdate: true,
            isContactModalOpen: true,
        });
    };

    hideContactModal = () => {
        this.setState({
            isContactModalOpen: false,
        });
    }
    
    getContacts = async () => {
        try {
            console.log("[getContacts] Invoking the back-end API to get all contacts of this tenant.");

            const contacts = await apiCalls.getContacts(this.props.authenticationContext);

            this.setState({
                contacts: contacts,
            });               
        }
        catch (err) {
            console.log(`[getContacts] Error retrieving the contacts: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
        finally {
            this.setState({
                isLoading: false,
            });
        }
    }

    handleContactDelete = (contact) => {
        this.setState({
            contactToDelete: contact,
        });
        this.showConfirmDeleteModal();
    }

    handleContactEdit = (contact) => {
        this.setState({
            contactToUpdate: contact,
        });
        this.showUpdateContactModal();
    }

    executeDeleteContact = async () => {
        try {
            console.log(`[deleteContact] Invoking the back-end API to delete the contact with Id ${this.state.contactToDelete.contactId}.`);

            await apiCalls.deleteContact(this.props.authenticationContext, this.state.contactToDelete.contactId);

            this.props.notificationContext.setNotification("Deleting contact.", `'${this.state.contactToDelete.name}' successfully deleted.`, 'success');

            this.getContacts();
        }
        catch (err) {
            this.props.notificationContext.setNotification("Deleting contact.", `Error deleting '${this.state.contactToDelete.name}'. ${err.toDetailedMessage()}`, 'danger');                  
        }

        this.hideConfirmDeleteModal();
    }

    executeAddOrUpdate = async (doneInfo) => {
        const contactAction = doneInfo.isUpdate ? 'updating' : 'adding';
        const contactActionDone = doneInfo.isUpdate ? 'updated' : 'added';

        try {
            console.log(`[executeAddOrUpdate] Invoking the back-end API ${contactAction} the contact.`);

            if (doneInfo.isUpdate)
            {
                await apiCalls.updateContact(this.props.authenticationContext, doneInfo.contactId, doneInfo.name, doneInfo.mobilePhoneNumber);
            }
            else
            {
                await apiCalls.addContact(this.props.authenticationContext, doneInfo.name, doneInfo.mobilePhoneNumber);
            }

            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(contactAction)} contact.`, `'${doneInfo.name}' successfully ${contactActionDone}.`, 'success');
            this.getContacts();
        }
        catch (err) {
            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(contactAction)} contact.`, `Error ${contactAction} '${doneInfo.name}'. ${err.toDetailedMessage()}`, 'danger');
        }
        finally {
            this.hideContactModal();
        }
    }

    shouldAddContactButtonBeEnabled = () => {
        if (this.state.contacts.length >= maximumNumberOfContacts) {
            return false;
        }

        return true;
    }

    // ------------------------------
    // Rendering starts here...
    // ------------------------------
    render () {
        const headerRendered = <h1>Contact settings</h1> 

        if (this.state.isLoading) {
            return (
                <Container fluid>
                    {headerRendered}
                    {this.props.tenantContext.showWarningWhenDisabled()}
                    <p><em>Loading...</em></p>
                </Container>
            );
        }

        const warningText = Utils.isTrue(this.state.contactToDelete.enabled) ? `The contact '${this.state.contactToDelete.name}' will no longer be notified of any events.` : "";
        
        const confirmDeleteModalRendered = <>
            <Modal show={this.state.isConfirmDeleteModalOpen} onHide={this.hideConfirmDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete contact '{this.state.contactToDelete.name}'?<br/>    
                    {warningText}           
                </Modal.Body>
                <Modal.Footer>
                    <Button color="secondary" onClick={this.hideConfirmDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={this.executeDeleteContact}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>

        const contacts = this.state.contacts;

        const contactsRendered = contacts.length > 0 ? 
            <ContactEntries contacts={contacts} handleDelete={this.handleContactDelete} handleEdit={this.handleContactEdit}/> : 
            <Alert className="mt-3" variant='warning' transition={false}>
                No contacts configured. Please add a contact which will be notified by the Zorgmessenger service.
            </Alert>;

        return (
            <Container fluid>
                {confirmDeleteModalRendered}
                <ModalContactAddOrUpdate isOpen={this.state.isContactModalOpen} isUpdate={this.state.isContactModalUpdate} tenant={this.props.tenantContext} contactToUpdate={this.state.contactToUpdate} onHide={this.hideContactModal} onAddOrUpdate={this.executeAddOrUpdate}/>
                {headerRendered}
                {this.props.tenantContext.showWarningWhenDisabled()}
                <hr/>
                <ButtonWithDisabledTooltip color="primary" disabled={!this.shouldAddContactButtonBeEnabled()} disabledTooltip="Maximum number of contacts reached" onClick={this.showAddContactModal}>Add contact</ButtonWithDisabledTooltip>
                {contactsRendered}
                <br/>
            </Container>
        );
    }
}

// Wrap the component so that multiple contexts are available via the props.
export const ContactSettings = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const notificationContext = useContext(NotificationContext);
    const tenantContext = useContext(TenantContext);
  
    return (
        <ContactSettingsInternal authenticationContext={authenticationContext} notificationContext={notificationContext} tenantContext={tenantContext} {...props}/>
    )
}